import { create } from "zustand";
import type { CollectionKind } from "@atelier/config";

export type { CollectionKind };

export type WizardState = {
  step: number;
  kind: CollectionKind;
  name: string;
  symbol: string;
  description: string;
  imageCid: string | undefined;
  bannerCid: string | undefined;
  // Drop fields
  openEdition: boolean;
  maxSupply: number;
  mintPriceEth: string;
  mintStart: string;
  mintEnd: string;
  maxPerWallet: number;
  baseUri: string;
  royaltyReceiver: string;
  royaltyBps: number;
  // Badge-specific fields
  badgeSubsidized: boolean;
  badgeSoulbound: boolean;
  badgeOneShot: boolean;
  badgeValidUntil: string;
  // Drop allowlist / presale fields
  allowlistEnabled: boolean;
  allowlistSigner: string; // worker-derived signer address; populated by Create page
  publicMintAt: string; // datetime-local string; when public mint opens
  // Drop reveal mechanic
  revealEnabled: boolean;
  placeholderCid: string | undefined; // IPFS CID of the placeholder JSON metadata
  // Drop blind-mint mode: when true the contract enforces a placeholder
  // and the real baseURI is never written to chain until reveal() is
  // called. PFP collections coming from /pfp set this on automatically.
  blindMint: boolean;
  // Pre-fill mechanism (used when arriving from the PFP studio)
  pfpProjectId: string | undefined;

  set: <K extends keyof WizardState>(k: K, v: WizardState[K]) => void;
  next: () => void;
  prev: () => void;
  reset: () => void;
  applyPreset: (preset: Partial<Omit<WizardState, "set" | "next" | "prev" | "reset" | "applyPreset">>) => void;
};

const initial: Omit<WizardState, "set" | "next" | "prev" | "reset" | "applyPreset"> = {
  step: 0,
  kind: "drop",
  name: "",
  symbol: "",
  description: "",
  imageCid: undefined,
  bannerCid: undefined,
  openEdition: false,
  maxSupply: 100,
  mintPriceEth: "0.01",
  mintStart: "",
  mintEnd: "",
  maxPerWallet: 5,
  baseUri: "",
  royaltyReceiver: "",
  royaltyBps: 500,
  badgeSubsidized: true,
  badgeSoulbound: true,
  badgeOneShot: true,
  badgeValidUntil: "",
  allowlistEnabled: false,
  allowlistSigner: "",
  publicMintAt: "",
  revealEnabled: false,
  placeholderCid: undefined,
  blindMint: false,
  pfpProjectId: undefined,
};

export const useWizard = create<WizardState>((set, get) => ({
  ...initial,
  set: (k, v) => set({ [k]: v } as Partial<WizardState>),
  next: () => set({ step: Math.min(3, get().step + 1) }),
  prev: () => set({ step: Math.max(0, get().step - 1) }),
  reset: () => set(initial),
  applyPreset: (preset) => set({ ...preset } as Partial<WizardState>),
}));
